import React from "react";
import { motion } from "framer-motion";
import { Maximize2, Lock, Mic, Bot, GraduationCap, Layers, FileText, Briefcase, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "../ui/dialog";
import { Reveal, Eyebrow, StaggerGroup, StaggerItem } from "./Reveal";
import { COURSE } from "../../data/course";

const FEATURE_ICONS = [Bot, Mic, GraduationCap, Layers, FileText, Briefcase];

function ShotCard({ shot, index }) {
  return (
    <StaggerItem>
      <Dialog>
        <DialogTrigger asChild>
          <button
            data-testid={`mock-shot-${index}`}
            className="card-glow group relative block w-full overflow-hidden rounded-2xl border border-[#293891]/10 bg-white text-left shadow-sm"
          >
            <div className="relative overflow-hidden bg-[#F5F7FC]">
              <img
                src={shot.img}
                alt={shot.caption}
                loading="lazy"
                className="aspect-[16/10] w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#293891]/40 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-[#293891] opacity-0 shadow-md transition-all duration-500 group-hover:opacity-100">
                <Maximize2 size={16} />
              </span>
            </div>
            <div className="px-5 py-4">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#e63329]">Preview {String(index + 1).padStart(2, "0")}</span>
              <p className="mt-1 font-display text-base font-medium leading-snug text-[#293891]">{shot.caption}</p>
            </div>
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-5xl border-[#293891]/10 bg-white p-2 sm:p-3">
          <DialogTitle className="sr-only">{shot.caption}</DialogTitle>
          <img src={shot.img} alt={shot.caption} className="h-auto w-full rounded-lg" />
        </DialogContent>
      </Dialog>
    </StaggerItem>
  );
}

export default function MockInterview() {
  const { mockInterview: m } = COURSE;
  return (
    <section id="mock-interviews" data-testid="mock-interviews-section" className="relative py-24 md:py-32">
      <div className="pointer-events-none absolute -left-24 top-16 h-[420px] w-[420px] glow-blue opacity-50" />
      <div className="pointer-events-none absolute right-0 bottom-1/4 h-[360px] w-[360px] glow-red opacity-30" />
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {/* header */}
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow>AI Mock Interviews</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2">
              <h2 className="font-display text-4xl font-semibold leading-[1.02] tracking-tight text-[#293891] sm:text-5xl">
                {m.product}
              </h2>
              <span className="rounded-full bg-[#3B82F6]/10 px-3 py-1 text-sm font-semibold text-[#3B82F6]">{m.by}</span>
            </div>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-4 font-display text-lg text-[#e63329] md:text-xl">{m.tagline}</p>
          </Reveal>
          <Reveal delay={0.24}>
            <p className="mt-5 text-base leading-relaxed text-[#566076]">{m.intro}</p>
          </Reveal>
        </div>

        {/* stats */}
        <StaggerGroup className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-[#293891]/10 bg-[#293891]/10 lg:grid-cols-4">
          {m.stats.map((s) => (
            <StaggerItem key={s.label} className="bg-white p-6 text-center md:p-8">
              <div className="font-display text-4xl font-semibold text-[#293891] md:text-5xl">{s.value}</div>
              <div className="mt-1.5 text-xs uppercase tracking-[0.14em] text-[#566076]">{s.label}</div>
            </StaggerItem>
          ))}
        </StaggerGroup>

        {/* 5-round journey */}
        <Reveal delay={0.1}>
          <div className="mt-12">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#566076]">The 5-Round Journey</span>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {m.rounds.map((r, i) => (
                <React.Fragment key={r}>
                  <span className="rounded-full border border-[#293891]/10 bg-white px-5 py-2.5 text-sm font-medium text-[#293891] shadow-sm">
                    <span className="mr-2 font-display text-[#3B82F6]">{i + 1}</span>
                    {r}
                  </span>
                  {i < m.rounds.length - 1 && <ArrowRight size={16} className="text-[#94A3B8]" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </Reveal>

        {/* features */}
        <StaggerGroup className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {m.features.map((f, i) => {
            const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
            return (
              <StaggerItem
                key={f.title}
                className="card-glow group rounded-3xl border border-[#293891]/10 bg-white p-7 shadow-sm"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#3B82F6]/10 text-[#3B82F6] transition-transform duration-500 group-hover:scale-110">
                  <Icon size={20} />
                </div>
                <h3 className="mt-5 font-display text-lg font-medium text-[#293891]">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#566076]">{f.desc}</p>
              </StaggerItem>
            );
          })}
        </StaggerGroup>

        {/* platform gallery */}
        <div className="mt-16">
          <Reveal>
            <h3 className="font-display text-2xl font-semibold text-[#293891]">Inside the platform</h3>
          </Reveal>
          <StaggerGroup className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {m.gallery.map((shot, i) => (
              <ShotCard key={shot.caption} shot={shot} index={i} />
            ))}
            {m.comingNote && (
              <StaggerItem>
                <motion.div
                  whileHover={{ y: -3 }}
                  data-testid="mock-upcoming"
                  className="flex h-full min-h-[240px] flex-col items-start justify-center gap-4 rounded-2xl border border-dashed border-[#293891]/25 bg-white/60 p-6 backdrop-blur-sm"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#3B82F6]/10 text-[#3B82F6]">
                    <Maximize2 size={22} />
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#293891]/10 bg-[#F5F7FC] px-3 py-1 text-[10px] font-medium uppercase tracking-[0.16em] text-[#566076]">
                    <Lock size={10} /> Coming Soon
                  </span>
                  <p className="text-sm leading-relaxed text-[#566076]">{m.comingNote}</p>
                </motion.div>
              </StaggerItem>
            )}
          </StaggerGroup>
        </div>
      </div>
    </section>
  );
}
